import React, { ReactNode, useState, useEffect } from "react";
import { Box, Typography, Drawer, IconButton, Collapse } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ArrowLeft,
  CheckCircle,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { palette } from "../../../../theme";
import { LessonUnit, LessonResponse } from "../../../../store/api/umbraco-api";
import { useSidebar } from "../../../../context/sidebar-context";
import { debugEnhancedLearningMinutes } from "../../../../utils/learning-time";

const formatLearningTime = (minutes: number): string => {
  if (minutes === 0) return "לא נלמד עדיין";
  if (minutes < 60) return `${minutes} דק'`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}:${remainingMinutes.toString().padStart(2, '0')} שעות`;
};

interface LessonSideBarProps extends Omit<LessonResponse, "id"> {
  units?: LessonUnit[];
  isMobile?: boolean;
  setSelectedUnit: (unit: LessonUnit) => void;
  selectedUnit: LessonUnit;
}

interface GroupedUnits {
  [key: string]: LessonUnit[];
}

const LessonSideBar = ({
  name,
  totalUnits = 0,
  completedUnits = 0,
  learningMinutes = 0,
  units = [],
  setSelectedUnit,
  selectedUnit,
}: LessonSideBarProps) => {
  const [expandedGroups, setExpandedGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [lastSelectedGroup, setLastSelectedGroup] = useState<string | null>(
    null
  );

  const groupedUnits = units.reduce((acc: GroupedUnits, unit) => {
    const groupName = unit.stepName || "Other";
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(unit);
    return acc;
  }, {});

  useEffect(() => {
    if (!selectedUnit || !selectedUnit.id) {
      return;
    }
    const selectedGroup = Object.entries(groupedUnits).find(([_, groupUnits]) =>
      groupUnits.some((unit) => unit.id === selectedUnit.id)
    )?.[0];

    if (selectedGroup && selectedGroup !== lastSelectedGroup) {
      setExpandedGroups((prev) => {
        const newState = { ...prev };
        if (lastSelectedGroup) {
          newState[lastSelectedGroup] = false;
        }
        newState[selectedGroup] = true;
        return newState;
      });
      setLastSelectedGroup(selectedGroup);
    }
  }, [selectedUnit?.id, groupedUnits, lastSelectedGroup]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  if (!selectedUnit || !selectedUnit.id) {
    return <></>;
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          borderBottom: "1px solid #E5E7EB",
          padding: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "14px",
            color: palette.black,
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: palette.purple,
          }}
        >
          {completedUnits}/{totalUnits}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          padding: 2,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            height: "10px",
            backgroundColor: palette.purple_light,
            borderRadius: "5px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${
                totalUnits > 0 ? (completedUnits / totalUnits) * 100 : 0
              }%`,
              height: "100%",
              backgroundColor: palette.purple,
              transition: "width 0.3s ease-in-out",
            }}
          />
        </Box>
        
        {/* Learning time display UI removed - functionality preserved */}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: 2,
        }}
      >
        {Object.entries(groupedUnits).map(([groupName, groupUnits]) => {
          if (Object.keys(groupedUnits).length === 1) {
            return (
              <Box
                key={groupName}
                sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
              >
                {groupUnits.map((unit) => (
                  <UnitListItem
                    key={unit.id}
                    unit={unit}
                    isCurrent={unit.id === selectedUnit.id}
                    onClick={() => setSelectedUnit(unit)}
                  />
                ))}
              </Box>
            );
          }

          return (
            <Box key={groupName}>
              <Box
                onClick={() => toggleGroup(groupName)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: palette.gray_light,
                  borderRadius: "8px",
                  cursor: "pointer",
                  marginBottom: "8px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: palette.black,
                  }}
                >
                  {groupName}
                </Typography>
                {expandedGroups[groupName] ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </Box>
              <Collapse in={expandedGroups[groupName]}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: "16px",
                  }}
                >
                  {groupUnits.map((unit) => (
                    <UnitListItem
                      key={unit.id}
                      unit={unit}
                      isCurrent={unit.id === selectedUnit.id}
                      onClick={() => setSelectedUnit(unit)}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

interface UnitListItemProps {
  unit: LessonUnit;
  isCurrent: boolean;
  onClick: () => void;
}

const UnitListItem = ({ unit, isCurrent, onClick }: UnitListItemProps) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: isCurrent
          ? palette.purple_light
          : palette.background,
        borderRadius: "12px",
        padding: "16px",
        cursor: "pointer",
        gap: 2,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: isCurrent
            ? palette.purple_light
            : palette.gray_light,
        },
      }}
    >
      <Box
        sx={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          backgroundColor: unit.isComplete
            ? palette.green_finish
            : palette.gray_light,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {unit.isComplete && (
          <CheckCircle
            sx={{
              fontSize: "14px",
              color: palette.white,
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: isCurrent ? 600 : 400,
          color: palette.black,
          flexGrow: 1,
        }}
      >
        {unit.name}
      </Typography>
    </Box>
  );
};

const MobileDrawer = ({ children }: { children: ReactNode }) => {
  const { isOpen, setIsOpen } = useSidebar();
  
  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Box sx={{ 
        padding: 2,
        height: '100%',
        width: '300px'
      }}>
        <IconButton
          onClick={() => setIsOpen(false)}
          sx={{ marginBottom: 2 }}
        >
          <CloseIcon />
        </IconButton>
        {children}
      </Box>
    </Drawer>
  );
};

const LessonSidebar = ({ isMobile = false, ...props }: LessonSideBarProps) => {
  if (isMobile) {
    return (
      <MobileDrawer>
        <LessonSideBar {...props} />
      </MobileDrawer>
    );
  }

  return <LessonSideBar {...props} />;
};

export default LessonSidebar;
